import { create } from 'zustand';
import { auth, ApiError, tryRefreshToken, type User } from '../lib/api';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticating: boolean;
  error: string | null;

  initialize: () => Promise<void>;
  login: (email: string) => Promise<void>;
  verify: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  clearSession: () => void;
  completeOnboarding: (data: Parameters<typeof auth.onboarding>[0]) => Promise<void>;
  setUser: (user: User | null) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: true,
  isAuthenticating: false,
  error: null,

  initialize: async () => {
    // 1. Try stored access token
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const { user } = await auth.me();
        set({ user, isLoading: false });
        return;
      } catch {
        localStorage.removeItem('access_token');
        // Fall through to cookie refresh
      }
    }

    // 2. Fallback: try httpOnly refresh cookie (survives localStorage clears)
    try {
      const refreshed = await tryRefreshToken();
      if (refreshed) {
        const { user } = await auth.me();
        set({ user, isLoading: false });
        return;
      }
    } catch { /* no cookie or expired */ }

    set({ user: null, isLoading: false });
  },

  clearSession: () => {
    localStorage.removeItem('access_token');
    set({ user: null, isLoading: false });
  },

  login: async (email) => {
    set({ isAuthenticating: true, error: null });
    try {
      await auth.login(email);
      set({ isAuthenticating: false });
    } catch (err) {
      // Auto-register if account not found — matches "enter email to create one" UX
      if (err instanceof ApiError && err.status === 404) {
        try {
          await auth.register(email, '');
          set({ isAuthenticating: false });
          return;
        } catch (regErr) {
          set({ isAuthenticating: false, error: regErr instanceof Error ? regErr.message : 'Registration failed' });
          throw regErr;
        }
      }
      set({ isAuthenticating: false, error: err instanceof Error ? err.message : 'Login failed' });
      throw err;
    }
  },

  verify: async (email, code) => {
    set({ isAuthenticating: true, error: null });
    try {
      const { accessToken, user } = await auth.verify(email, code);
      localStorage.setItem('access_token', accessToken);
      set({ user, isAuthenticating: false });
    } catch (err) {
      set({ isAuthenticating: false, error: err instanceof Error ? err.message : 'Invalid code' });
      throw err;
    }
  },

  logout: async () => {
    try { await auth.logout(); } catch { /* ignore */ }
    localStorage.removeItem('access_token');
    set({ user: null });
  },

  completeOnboarding: async (data) => {
    const res = await auth.onboarding(data);
    if (res.user) set({ user: res.user });
  },

  setUser: (user) => set({ user }),
}));

// Auto-initialize on import
useAuthStore.getState().initialize();
